import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
} from 'react'
import { authAPI } from '../services/api'
import { useAuth } from './useAuth'

const ThemeContext = createContext()

// VH-133: 'system' is a stored choice, not the absence of one. It means "keep
// following the operating system" — a state a boolean cannot express and,
// once left, could never be returned to. Kept in sync with VALID_THEMES in
// backend/app/schemas/auth.py, which rejects anything else with a 422.
// eslint-disable-next-line react-refresh/only-export-components -- shared with the provider below
export const THEMES = ['light', 'dark', 'system']

// Mirrors the profile value. The profile is the source of truth, but it
// arrives over the network — this is what the pre-paint script in index.html
// reads to avoid a flash of the wrong theme on every load.
const STORAGE_KEY = 'vh-theme'

const DARK_QUERY = '(prefers-color-scheme: dark)'

// Every localStorage and matchMedia access is guarded. localStorage throws in
// private-mode Safari and when cookies are blocked; matchMedia is absent in
// jsdom without a shim. Neither may be able to break the app over a colour.
const readStored = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return THEMES.includes(stored) ? stored : null
  } catch {
    return null
  }
}

const writeStored = (theme) => {
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    /* Preference simply will not survive a reload on this device. */
  }
}

const prefersDark = () => {
  try {
    return window.matchMedia?.(DARK_QUERY).matches ?? false
  } catch {
    return false
  }
}

/** Which of the two palettes a preference actually renders as, right now. */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper, co-located with its only consumer
export const resolveTheme = (preference) =>
  preference === 'system' || !preference
    ? prefersDark()
      ? 'dark'
      : 'light'
    : preference

/**
 * Paint the choice. Both hooks are set: the class drives Tailwind's `dark:`
 * variant, the attribute is there for anything that needs to read the current
 * theme from the DOM (tests, and the chart theming in useChartTheme).
 */
const applyTheme = (resolved) => {
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.setAttribute('data-theme', resolved)
}

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth()

  // Seeded from the same source the pre-paint script used, so React's first
  // render agrees with what is already on screen. Starting from a constant
  // would re-introduce the flash this design exists to prevent.
  const [preference, setPreference] = useState(() => readStored() ?? 'system')
  const [resolved, setResolved] = useState(() => resolveTheme(readStored()))

  // Guards the profile adoption below so it happens once per login, not on
  // every profile refresh — otherwise a toggle made on this device would be
  // reverted by the next fetch that returns the older server value.
  const adoptedForUser = useRef(null)

  // The profile is the source of truth. Once the user resolves, their stored
  // choice wins over whatever this device had cached — that is what makes the
  // preference follow them from phone to desktop.
  useEffect(() => {
    if (!user) {
      adoptedForUser.current = null
      return
    }
    if (adoptedForUser.current === user.id) return
    adoptedForUser.current = user.id

    const serverTheme = THEMES.includes(user.theme) ? user.theme : null
    if (serverTheme && serverTheme !== preference) {
      setPreference(serverTheme)
      writeStored(serverTheme)
    }
  }, [user, preference])

  // Repaint whenever the preference changes.
  useEffect(() => {
    const next = resolveTheme(preference)
    setResolved(next)
    applyTheme(next)
  }, [preference])

  // Follow the OS live, but only while the user is actually following it. An
  // explicit choice must not be overridden by the operating system.
  useEffect(() => {
    if (preference !== 'system') return
    let media
    try {
      media = window.matchMedia?.(DARK_QUERY)
    } catch {
      return
    }
    if (!media?.addEventListener) return

    const onChange = () => {
      const next = prefersDark() ? 'dark' : 'light'
      setResolved(next)
      applyTheme(next)
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [preference])

  /**
   * Optimistic by design: paint and cache immediately, persist in the
   * background. The toggle has to feel instant, and a failed write is not
   * worth interrupting the user for — the theme is correct on this device for
   * the session, it simply will not follow them to another one.
   */
  const setTheme = useCallback(
    (next) => {
      if (!THEMES.includes(next)) return
      setPreference(next)
      writeStored(next)

      if (!user) return
      authAPI.updateProfile({ theme: next }).catch(() => {
        /* Intentionally silent — see above. */
      })
    },
    [user]
  )

  /** What the visible toggle does: flip the palette you are looking at. */
  const toggleTheme = useCallback(() => {
    setTheme(resolved === 'dark' ? 'light' : 'dark')
  }, [resolved, setTheme])

  return (
    <ThemeContext.Provider
      value={{ preference, theme: resolved, setTheme, toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- hook must be co-located with its provider
export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

/**
 * The theme, or null when there is no provider above.
 *
 * Anything that *controls* the theme must be inside the provider, and
 * `useTheme` throwing is how that stays true. But a component that merely
 * wants to know which palette is on screen — a chart choosing its axis colour,
 * say — should still render when mounted outside the app shell: in a test, in
 * an export subtree, in an isolated preview. For those, absence of a provider
 * is a fact to handle, not an error.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook must be co-located with its provider
export const useThemeOptional = () => useContext(ThemeContext) ?? null
